// ============ عناوين ووصف وبيانات منظمة (JSON-LD) لكل صفحة بكل لغة ============
// نفس الملف بيستخدمه الموقع وهو شغال (useSeo) وسكريبت البناء اللي بيعمل صفحات HTML جاهزة لمحركات البحث.
import { COMPANY, SITE_URL } from "../Data/Company";
import { DEV_DETAILS, getDeveloper, pick } from "../Data/Developersdata";
import { PROJECTS } from "../Data/Projectsdata";
import { LANGUAGES, absUrl, getLanguage } from "../Utility/i18nPaths";

export const STATIC_ROUTES = ["/", "/about-us", "/projects", "/off-plan-guide", "/blogs", "/contact-us"];
export const ROUTES = [...STATIC_ROUTES, ...DEV_DETAILS.map((d) => `/developers/${d.slug}`)];

const fill = (s, vars) => String(s || "").replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ""));

const ORG_ID = `${SITE_URL}/#organization`;
const SITE_ID = `${SITE_URL}/#website`;

const DAY_URL = (d) => `https://schema.org/${d}`;

export const organization = (t, lang) => ({
  "@type": "RealEstateAgent",
  "@id": ORG_ID,
  name: COMPANY.name,
  alternateName: [COMPANY.shortName, "هيلماكس العقارية"],
  description: t.seo.org,
  url: `${SITE_URL}/`,
  logo: { "@type": "ImageObject", url: `${SITE_URL}${COMPANY.logo}` },
  image: `${SITE_URL}${COMPANY.ogImage}`,
  telephone: COMPANY.phone,
  email: COMPANY.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: `${COMPANY.address.office}, ${COMPANY.address.building}, ${COMPANY.address.street}`,
    addressLocality: COMPANY.address.city,
    addressRegion: COMPANY.address.city,
    addressCountry: COMPANY.address.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: COMPANY.geo.lat, longitude: COMPANY.geo.lng },
  hasMap: COMPANY.mapUrl,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: COMPANY.hours.days.map(DAY_URL),
      opens: COMPANY.hours.opens,
      closes: COMPANY.hours.closes,
    },
  ],
  areaServed: { "@type": "City", name: "Dubai" },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: COMPANY.phone,
    contactType: "sales",
    areaServed: "AE",
    availableLanguage: LANGUAGES.map((l) => l.label),
  },
  sameAs: [COMPANY.mapUrl, ...COMPANY.socials.map((s) => s.href)],
  knowsAbout: ["Off-plan property", "Dubai real estate", ...DEV_DETAILS.map((d) => d.name.en)],
  inLanguage: lang,
});

const website = () => ({
  "@type": "WebSite",
  "@id": SITE_ID,
  url: `${SITE_URL}/`,
  name: COMPANY.name,
  publisher: { "@id": ORG_ID },
  inLanguage: LANGUAGES.map((l) => l.code),
});

const breadcrumbs = (t, lang, items) => ({
  "@type": "BreadcrumbList",
  itemListElement: [{ name: t.seo.home_crumb, path: "/" }, ...items].map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: absUrl(lang, c.path),
  })),
});

const money = (v) => (typeof v === "number" ? `AED ${v.toLocaleString("en-US")}` : null);

const projectItem = (pr, lang, t) => {
  const dev = getDeveloper(pr.dev);
  const bits = [
    `${pick(pr.type, lang)} · ${pick(pr.area, lang)}`,
    dev && pick(dev.name, lang),
    money(pr.price) && `${t.projects.from} ${money(pr.price)}`,
    pr.y && `${t.projects.handover}: ${pr.q ? `${t.projects.quarters[pr.q - 1]} ` : ""}${pr.y}`,
  ].filter(Boolean);
  return {
    "@type": "Residence",
    name: pick(pr.name, lang),
    description: bits.join(" · "),
    address: {
      "@type": "PostalAddress",
      addressLocality: pick(pr.area, "en"),
      addressRegion: pr.area.en.includes("Abu Dhabi") ? "Abu Dhabi" : "Dubai",
      addressCountry: "AE",
    },
  };
};

const itemList = (name, items) => ({
  "@type": "ItemList",
  name,
  numberOfItems: items.length,
  itemListElement: items.map((item, i) => ({ "@type": "ListItem", position: i + 1, item })),
});

/**
 * بيانات الصفحة: title / description / canonical / hreflang / JSON-LD
 * path = الرابط من غير بادئة اللغة (مثلاً /about-us)
 */
export const pageMeta = (t, lang, path) => {
  const s = t.seo;
  const url = absUrl(lang, path);
  let title = s.home.title;
  let description = s.home.description;
  let pageType = "WebPage";
  let crumbs = [];
  const extra = [];
  const nav = t.nav.links;

  if (path === "/about-us") {
    ({ title, description } = s.about);
    pageType = "AboutPage";
    crumbs = [{ name: nav.about, path }];
  } else if (path === "/projects") {
    title = s.projects.title;
    description = fill(s.projects.description, { count: PROJECTS.length, devs: DEV_DETAILS.length });
    pageType = "CollectionPage";
    crumbs = [{ name: nav.projects, path }];
    extra.push(itemList(t.projectsPage.label, PROJECTS.map((pr) => projectItem(pr, lang, t))));
  } else if (path === "/blogs") {
    ({ title, description } = s.blogs);
    pageType = "CollectionPage";
    crumbs = [{ name: nav.blogs, path }];
  } else if (path === "/contact-us") {
    ({ title, description } = s.contact);
    pageType = "ContactPage";
    crumbs = [{ name: nav.contact, path }];
    extra.push({
      "@type": "FAQPage",
      mainEntity: t.contactPage.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  } else if (path === "/off-plan-guide") {
    const g = t.guidePage;
    ({ title, description } = s.guide);
    pageType = "WebPage";
    crumbs = [{ name: nav.guide, path }];
    extra.push({
      "@type": "HowTo",
      name: `${g.title.join(" ")}`,
      description: g.intro,
      step: g.steps.map((st, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: st.title,
        text: st.text,
        url: `${url}#step-${i + 1}`,
      })),
    });
    extra.push({
      "@type": "FAQPage",
      mainEntity: g.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  } else if (path.startsWith("/developers/")) {
    const dev = getDeveloper(path.split("/")[2]);
    if (dev) {
      const name = pick(dev.name, lang);
      title = fill(s.developer.title, { name });
      description = fill(s.developer.description, { name, since: dev.since });
      pageType = "ProfilePage";
      crumbs = [
        { name: nav.developers, path: "/" },
        { name, path },
      ];
      extra.push({
        "@type": "Organization",
        "@id": `${url}#developer`,
        name: dev.name.en,
        alternateName: dev.name.ar,
        url: dev.website,
        foundingDate: String(dev.since),
        description: pick(dev.about, lang),
      });
      extra.push(
        itemList(
          `${name} · ${t.devPage.recentLabel}`,
          PROJECTS.filter((pr) => pr.dev === dev.slug).map((pr) => projectItem(pr, lang, t))
        )
      );
    }
  } else if (path === "/") {
    extra.push(
      itemList(
        t.developers.label,
        DEV_DETAILS.map((d) => ({ "@type": "Organization", name: d.name.en, url: d.website }))
      )
    );
  }

  const webpage = {
    "@type": pageType,
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: lang,
    isPartOf: { "@id": SITE_ID },
    about: { "@id": ORG_ID },
    primaryImageOfPage: `${SITE_URL}${COMPANY.ogImage}`,
  };
  if (crumbs.length) webpage.breadcrumb = breadcrumbs(t, lang, crumbs);
  if (pageType === "ProfilePage") webpage.mainEntity = { "@id": `${url}#developer` };

  return {
    lang,
    path,
    title,
    description,
    url,
    image: `${SITE_URL}${COMPANY.ogImage}`,
    locale: getLanguage(lang).og,
    alternates: LANGUAGES.map((l) => ({ hreflang: l.hreflang || l.code, href: absUrl(l.code, path) })).concat({
      hreflang: "x-default",
      href: absUrl("en", path),
    }),
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [organization(t, lang), website(), webpage, ...extra],
    },
  };
};

// وسوم الـ head كنص HTML (لسكريبت البناء)
const esc = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const headTags = (m) => {
  const og = LANGUAGES.filter((l) => l.code !== m.lang).map(
    (l) => `<meta data-seo property="og:locale:alternate" content="${l.og}">`
  );
  return [
    `<meta data-seo name="description" content="${esc(m.description)}">`,
    `<meta data-seo name="robots" content="index,follow,max-image-preview:large">`,
    `<link data-seo rel="canonical" href="${m.url}">`,
    ...m.alternates.map((a) => `<link data-seo rel="alternate" hreflang="${a.hreflang}" href="${a.href}">`),
    `<meta data-seo property="og:type" content="website">`,
    `<meta data-seo property="og:site_name" content="${esc(COMPANY.name)}">`,
    `<meta data-seo property="og:title" content="${esc(m.title)}">`,
    `<meta data-seo property="og:description" content="${esc(m.description)}">`,
    `<meta data-seo property="og:url" content="${m.url}">`,
    `<meta data-seo property="og:image" content="${m.image}">`,
    `<meta data-seo property="og:locale" content="${m.locale}">`,
    ...og,
    `<meta data-seo name="twitter:card" content="summary_large_image">`,
    `<meta data-seo name="twitter:title" content="${esc(m.title)}">`,
    `<meta data-seo name="twitter:description" content="${esc(m.description)}">`,
    `<meta data-seo name="twitter:image" content="${m.image}">`,
    `<script data-seo type="application/ld+json">${JSON.stringify(m.jsonLd).replace(/</g, "\\u003c")}</script>`,
  ].join("");
};
